import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronRight,
  ChevronDown,
  Pencil,
  Trash2,
  Lock,
  Plus,
  FolderTree,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const DOYENCE_COMPANY_ID = "2c1cc79d-80e5-4890-8a33-bd0d28ebaead";
const MAX_DEPTH_INDEX = 3;

interface CategoryRow {
  id: string;
  name: string;
  parent_id: string | null;
  company_id: string | null;
  display_order: number;
  depth: number;
}

interface TreeNode extends CategoryRow {
  children: TreeNode[];
}

function buildTree(rows: CategoryRow[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  rows.forEach((r) => map.set(r.id, { ...r, children: [] }));
  const roots: TreeNode[] = [];
  map.forEach((node) => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

function getDescendantIds(rows: CategoryRow[], id: string): string[] {
  const childrenByParent = new Map<string, CategoryRow[]>();
  rows.forEach((r) => {
    if (r.parent_id) {
      const arr = childrenByParent.get(r.parent_id) ?? [];
      arr.push(r);
      childrenByParent.set(r.parent_id, arr);
    }
  });
  const out: string[] = [];
  const walk = (nid: string) => {
    const kids = childrenByParent.get(nid) ?? [];
    kids.forEach((k) => {
      out.push(k.id);
      walk(k.id);
    });
  };
  walk(id);
  return out;
}

type DialogMode =
  | { kind: "create"; parent: CategoryRow | null }
  | { kind: "edit"; node: CategoryRow };

export default function BrowseCategories() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const adminId = session?.user?.id ?? null;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandedInitialised, setExpandedInitialised] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [formName, setFormName] = useState("");
  const [formOrder, setFormOrder] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [deleteOpenId, setDeleteOpenId] = useState<string | null>(null);

  const treeQuery = useQuery({
    queryKey: ["browse-categories-tree"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_browse_category_tree", {
        p_company_id: DOYENCE_COMPANY_ID,
      });
      if (error) throw error;
      const rows = (data ?? []) as Array<{
        id: string;
        name: string;
        parent_id: string | null;
        company_id: string | null;
        display_order: number;
        depth: number;
        is_global: boolean;
      }>;
      return rows
        .filter((r) => r.company_id === null)
        .map<CategoryRow>((r) => ({
          id: r.id,
          name: r.name,
          parent_id: r.parent_id,
          company_id: r.company_id,
          display_order: r.display_order,
          depth: r.depth,
        }));
    },
  });

  const countsQuery = useQuery({
    queryKey: ["browse-category-counts"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_item_count_per_browse_category", {
        p_company_id: DOYENCE_COMPANY_ID,
        p_include_system: true,
      });
      if (error) throw error;
      const map = new Map<string, number>();
      (data ?? []).forEach((r: { browse_category_id: string; item_count: number }) => {
        map.set(r.browse_category_id, Number(r.item_count) || 0);
      });
      return map;
    },
  });

  const rows = treeQuery.data ?? [];
  const counts = countsQuery.data ?? new Map<string, number>();
  const tree = useMemo(() => buildTree(rows), [rows]);
  const childrenCount = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => {
      if (r.parent_id) m.set(r.parent_id, (m.get(r.parent_id) ?? 0) + 1);
    });
    return m;
  }, [rows]);

  useEffect(() => {
    if (!expandedInitialised && tree.length > 0) {
      setExpanded(new Set(tree.map((n) => n.id)));
      setExpandedInitialised(true);
    }
  }, [tree, expandedInitialised]);

  useEffect(() => {
    const channel = supabase
      .channel("admin-browse-categories")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "browse_categories" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["browse-categories-tree"] });
          queryClient.invalidateQueries({ queryKey: ["browse-category-counts"] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = (parent: CategoryRow | null) => {
    setFormName("");
    setFormOrder(0);
    setDialogMode({ kind: "create", parent });
  };

  const openEdit = (node: CategoryRow) => {
    setFormName(node.name);
    setFormOrder(node.display_order);
    setDialogMode({ kind: "edit", node });
  };

  const closeDialog = () => {
    if (submitting) return;
    setDialogMode(null);
  };

  const parentForDialog: CategoryRow | null = useMemo(() => {
    if (!dialogMode) return null;
    if (dialogMode.kind === "create") return dialogMode.parent;
    const pid = dialogMode.node.parent_id;
    return pid ? rows.find((r) => r.id === pid) ?? null : null;
  }, [dialogMode, rows]);

  const resultingDepth = useMemo(() => {
    if (!dialogMode) return 0;
    if (dialogMode.kind === "edit") return dialogMode.node.depth;
    return dialogMode.parent ? dialogMode.parent.depth + 1 : 0;
  }, [dialogMode]);

  const editingDescendantMaxRelativeDepth = useMemo(() => {
    if (!dialogMode || dialogMode.kind !== "edit") return 0;
    const node = dialogMode.node;
    const ids = getDescendantIds(rows, node.id);
    const byId = new Map(rows.map((r) => [r.id, r]));
    let maxRel = 0;
    ids.forEach((cid) => {
      const c = byId.get(cid);
      if (c) {
        const rel = c.depth - node.depth;
        if (rel > maxRel) maxRel = rel;
      }
    });
    return maxRel;
  }, [dialogMode, rows]);

  const wouldExceedDepth =
    resultingDepth > MAX_DEPTH_INDEX ||
    (dialogMode?.kind === "edit"
      ? resultingDepth + editingDescendantMaxRelativeDepth > MAX_DEPTH_INDEX
      : false);

  const writeAudit = async (
    action: "browse_category_created" | "browse_category_updated" | "browse_category_deleted",
    entityId: string,
    details: Record<string, unknown>
  ) => {
    if (!adminId) return;
    await supabase.from("admin_audit_logs").insert({
      admin_id: adminId,
      action,
      entity_type: "browse_category",
      entity_id: entityId,
      details: details as never,
    });
  };

  const handleSubmit = async () => {
    if (!dialogMode) return;
    const trimmed = formName.trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    if (trimmed.length > 100) {
      toast.error("Name must be 100 characters or fewer");
      return;
    }
    if (resultingDepth > MAX_DEPTH_INDEX) {
      toast.error("Maximum category depth is 4 levels.");
      return;
    }
    if (
      dialogMode.kind === "edit" &&
      resultingDepth + editingDescendantMaxRelativeDepth > MAX_DEPTH_INDEX
    ) {
      toast.error("This change would push a descendant category beyond the maximum depth of 4.");
      return;
    }
    setSubmitting(true);
    try {
      if (dialogMode.kind === "edit") {
        const editing = dialogMode.node;
        const { error } = await supabase
          .from("browse_categories")
          .update({
            name: trimmed,
            display_order: Number(formOrder) || 0,
          })
          .eq("id", editing.id)
          .select();
        if (error) throw error;
        await writeAudit("browse_category_updated", editing.id, {
          old_name: editing.name,
          new_name: trimmed,
          old_parent_id: editing.parent_id,
          new_parent_id: editing.parent_id,
        });
        toast.success("Category updated");
      } else {
        const parentId = dialogMode.parent?.id ?? null;
        const { data, error } = await supabase
          .from("browse_categories")
          .insert({
            name: trimmed,
            parent_id: parentId,
            company_id: null,
            display_order: Number(formOrder) || 0,
          })
          .select()
          .single();
        if (error) throw error;
        await writeAudit("browse_category_created", data.id, {
          name: data.name,
          parent_id: data.parent_id,
          depth: data.depth,
        });
        toast.success("Category created");
      }
      setDialogMode(null);
      queryClient.invalidateQueries({ queryKey: ["browse-categories-tree"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save category";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (node: CategoryRow) => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("browse_categories")
        .delete()
        .eq("id", node.id)
        .select();
      if (error) throw error;
      await writeAudit("browse_category_deleted", node.id, {
        name: node.name,
        depth: node.depth,
      });
      toast.success("Category deleted");
      setDeleteOpenId(null);
      queryClient.invalidateQueries({ queryKey: ["browse-categories-tree"] });
      queryClient.invalidateQueries({ queryKey: ["browse-category-counts"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reorderSiblings = async (siblings: TreeNode[], oldIndex: number, newIndex: number) => {
    const reordered = arrayMove(siblings, oldIndex, newIndex);
    try {
      for (let i = 0; i < reordered.length; i++) {
        const node = reordered[i];
        if (node.display_order === i) continue;
        const { error } = await supabase
          .from("browse_categories")
          .update({ display_order: i })
          .eq("id", node.id)
          .select();
        if (error) throw error;
      }
      queryClient.invalidateQueries({ queryKey: ["browse-categories-tree"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to reorder";
      toast.error(msg);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  interface NodeRowProps {
    node: TreeNode;
    siblings: TreeNode[];
  }

  const NodeRow = ({ node, siblings }: NodeRowProps) => {
    const sortable = useSortable({ id: node.id });
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded.has(node.id);
    const itemCount = counts.get(node.id) ?? 0;
    const subCount = childrenCount.get(node.id) ?? 0;
    const canDelete = subCount === 0 && itemCount === 0;

    return (
      <div ref={setNodeRef} style={style}>
        <div
          className="group flex items-center gap-2 py-1.5 px-2 rounded-sm hover:bg-slate-800/60 cursor-pointer"
          style={{ paddingLeft: `${node.depth * 16 + 8}px` }}
        >
          <button
            type="button"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="h-5 w-5 flex items-center justify-center text-slate-500 hover:text-slate-200 cursor-grab active:cursor-grabbing"
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) toggle(node.id);
            }}
            className="h-5 w-5 flex items-center justify-center text-slate-400 hover:text-white"
          >
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )
            ) : (
              <span className="inline-block h-4 w-4" />
            )}
          </button>
          <span
            className={
              "flex-1 truncate " +
              (node.depth === 0 ? "font-semibold text-white" : "text-slate-200")
            }
          >
            {node.name}
          </span>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
            L{node.depth + 1}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800/70 text-slate-400 border border-slate-700/50">
            {itemCount} item{itemCount === 1 ? "" : "s"}
          </span>
          {node.depth < MAX_DEPTH_INDEX && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCreate(node);
                    }}
                    className="h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Add child category"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Add child category</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <div
            className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-slate-300 hover:bg-slate-700 hover:text-white"
                    onClick={() => openEdit(node)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
              {canDelete ? (
                <Popover
                  open={deleteOpenId === node.id}
                  onOpenChange={(o) => setDeleteOpenId(o ? node.id : null)}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-400 hover:bg-red-500/15 hover:text-red-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 bg-slate-900 border-slate-700 text-slate-100">
                    <p className="text-sm mb-3">
                      Delete '<span className="font-semibold">{node.name}</span>'? This cannot be
                      undone.
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteOpenId(null)}
                        className="text-slate-300 hover:bg-slate-700"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDelete(node)}
                        disabled={submitting}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Confirm
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex h-7 w-7 items-center justify-center text-slate-500">
                      <Lock className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {subCount > 0
                      ? `Cannot delete: this category has ${subCount} subcategor${
                          subCount === 1 ? "y" : "ies"
                        }. Remove them first.`
                      : `Cannot delete: ${itemCount} system item${
                          itemCount === 1 ? "" : "s"
                        } are assigned to this category. Reassign them first.`}
                  </TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <SiblingList nodes={node.children} />
        )}
      </div>
    );
  };

  const SiblingList = ({ nodes }: { nodes: TreeNode[] }) => {
    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = nodes.findIndex((n) => n.id === active.id);
      const newIndex = nodes.findIndex((n) => n.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      void reorderSiblings(nodes, oldIndex, newIndex);
    };
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={nodes.map((n) => n.id)} strategy={verticalListSortingStrategy}>
          <div>
            {nodes.map((n) => (
              <NodeRow key={n.id} node={n} siblings={nodes} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    );
  };

  const parentLabel = parentForDialog?.name ?? null;
  const dialogTitle = !dialogMode
    ? ""
    : dialogMode.kind === "edit"
    ? "Edit Category"
    : parentLabel
    ? `Add category under ${parentLabel}`
    : "Add root category";

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-white">Browse Categories</h2>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Manage the global category tree used for navigating the estimate item catalog. These
              categories are visible to all companies.
            </p>
          </div>
          <Button
            onClick={() => openCreate(null)}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Category
          </Button>
        </div>

        <div
          className="bg-slate-900 border border-slate-800 rounded-md p-2 min-h-[300px]"
          onClick={(e) => {
            if (e.target === e.currentTarget) openCreate(null);
          }}
        >
          {treeQuery.isLoading ? (
            <div className="p-6 text-sm text-slate-400">Loading…</div>
          ) : treeQuery.error ? (
            <div className="p-6 text-sm text-red-400">
              {(treeQuery.error as Error).message}
            </div>
          ) : tree.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rounded-full bg-slate-800 p-4 mb-3">
                <FolderTree className="h-7 w-7 text-slate-400" />
              </div>
              <p className="text-sm text-slate-300 font-medium mb-1">No categories yet.</p>
              <p className="text-xs text-slate-500 mb-4">
                Add your first root category to get started.
              </p>
              <Button
                onClick={() => openCreate(null)}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add Category
              </Button>
            </div>
          ) : (
            <>
              <SiblingList nodes={tree} />
              <div
                className="h-12"
                onClick={(e) => {
                  e.stopPropagation();
                  openCreate(null);
                }}
              />
            </>
          )}
        </div>
      </div>

      <Dialog open={!!dialogMode} onOpenChange={(o) => (o ? null : closeDialog())}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{dialogTitle}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Global navigation category visible to all companies.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label htmlFor="cat-name" className="text-slate-200">
                Name
              </Label>
              <Input
                id="cat-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                maxLength={100}
                placeholder="e.g. Electrical"
                className="bg-slate-950 border-slate-700 text-slate-100"
                autoFocus
              />
              <p className="text-xs text-slate-500">{formName.length}/100</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">Parent Category</Label>
              <div className="px-3 py-2 rounded-sm bg-slate-950 border border-slate-700 text-sm text-slate-300">
                {parentLabel ?? "None (root category)"}
              </div>
              <div className="text-xs text-slate-400">
                This will be a Level {resultingDepth + 1} category
              </div>
              {resultingDepth === MAX_DEPTH_INDEX && !wouldExceedDepth && (
                <div className="text-xs text-amber-400">
                  This will be the deepest allowed level (L{MAX_DEPTH_INDEX + 1}). No children can
                  be added under it.
                </div>
              )}
              {wouldExceedDepth && (
                <div className="text-xs text-red-400">
                  {dialogMode?.kind === "edit"
                    ? "This change would push a descendant category beyond the maximum depth of 4."
                    : "Maximum category depth is 4 levels."}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cat-order" className="text-slate-200">
                Display Order
              </Label>
              <Input
                id="cat-order"
                type="number"
                value={formOrder}
                onChange={(e) => setFormOrder(parseInt(e.target.value, 10) || 0)}
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
              <p className="text-xs text-slate-500">
                Controls sort order within the same parent level. Lower numbers appear first.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={closeDialog}
              disabled={submitting}
              className="text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formName.trim() || wouldExceedDepth}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
            >
              {submitting
                ? "Saving…"
                : dialogMode?.kind === "edit"
                ? "Save Changes"
                : "Create Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
